import { forwardRef, useEffect, useState, type ChangeEvent } from 'react';
import PlacesAutocomplete, {
  geocodeByAddress,
  geocodeByPlaceId
} from 'react-places-autocomplete';
import type {
  PlacesAutocompleteRenderProps,
  Suggestion
} from 'react-places-autocomplete';
import { useApiIsLoaded } from '@vis.gl/react-google-maps';
import { Input, type InputProps } from '../../base/input/input';

type AddressAutocompleteFieldProps = Omit<InputProps, 'ref'> & {
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void;
  searchOptions?: Partial<google.maps.places.AutocompletionRequest>;
};

const AddressAutocompleteFieldComponent = forwardRef<
  HTMLInputElement,
  AddressAutocompleteFieldProps
>(
  (
    {
      onPlaceSelected,
      isDisabled,
      value,
      onChange,
      labelClassName,
      label,
      searchOptions,
      ...props
    },
    ref
  ) => {
    const isLoaded = useApiIsLoaded();
    const [address, setAddress] = useState((value as string) || '');

    useEffect(() => {
      const nextValue = (value as string) || '';
      setAddress((prev) => (prev === nextValue ? prev : nextValue));
    }, [value]);

    const handleChange = (newAddress: string) => {
      setAddress(newAddress);
      if (onChange) {
        onChange(newAddress);
      }
    };

    const handleSelect = async (selectedAddress: string, placeId?: string) => {
      setAddress(selectedAddress);
      if (onChange) {
        onChange(selectedAddress);
      }

      try {
        const results = placeId
          ? await geocodeByPlaceId(placeId)
          : await geocodeByAddress(selectedAddress);
        if (results && results.length > 0) {
          const result = results[0];
          // Cast GeocoderResult to PlaceResult as they are compatible for our usage
          onPlaceSelected(result as unknown as google.maps.places.PlaceResult);
        }
      } catch (_error) {
        // TODO: surface address lookup failures through monitoring once available.
      }
    };

    if (!isLoaded) {
      return (
        <Input
          {...props}
          isDisabled
          label={label}
          placeholder="Loading address search..."
          ref={ref}
          value={address}
          onChange={() => {}}
          labelClassName={labelClassName}
        />
      );
    }

    return (
      <PlacesAutocomplete
        value={address}
        onChange={handleChange}
        onSelect={handleSelect}
        // @ts-expect-error searchOptions is supported by the library but missing from its types
        searchOptions={searchOptions}
      >
        {({
          getInputProps,
          suggestions,
          getSuggestionItemProps,
          loading
        }: PlacesAutocompleteRenderProps) => {
          const { onChange: placesOnChange, ...inputProps } = getInputProps({
            placeholder: props.placeholder || 'Search for an address',
            autoComplete: 'off',
            disabled: isDisabled
          });

          const handleInputChange = (
            valueOrEvent: string | ChangeEvent<HTMLInputElement>
          ) => {
            if (typeof valueOrEvent === 'string') {
              // Handle react-aria TextField (string)
              placesOnChange({
                target: {
                  value: valueOrEvent
                } as EventTarget & HTMLInputElement
              } as ChangeEvent<HTMLInputElement>);
            } else {
              // Handle standard input (event)
              placesOnChange(valueOrEvent);
            }
          };

          return (
            <div className="relative">
              <Input
                {...inputProps}
                ref={ref}
                {...props}
                onChange={handleInputChange}
                value={address}
                labelClassName={labelClassName}
                label={label}
              />
              {suggestions.length > 0 && (
                <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {loading && (
                    <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                      Loading...
                    </div>
                  )}
                  {suggestions.map((suggestion: Suggestion) => {
                    const className = suggestion.active
                      ? 'relative cursor-pointer select-none bg-gray-100 px-4 py-2 text-gray-900'
                      : 'relative cursor-pointer select-none px-4 py-2 text-gray-900';

                    const suggestionKey =
                      suggestion.placeId ||
                      suggestion.description ||
                      `${suggestion.index}-${suggestion.formattedSuggestion.mainText}`;

                    const { key: _ignoredKey, ...suggestionProps } =
                      getSuggestionItemProps(suggestion, {
                        className
                      });

                    return (
                      <div key={suggestionKey} {...suggestionProps}>
                        <span className="block truncate font-medium">
                          {suggestion.formattedSuggestion.mainText}
                        </span>
                        <span className="block truncate text-xs text-gray-500">
                          {suggestion.formattedSuggestion.secondaryText}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }}
      </PlacesAutocomplete>
    );
  }
);

AddressAutocompleteFieldComponent.displayName = 'AddressAutocompleteField';

export type { AddressAutocompleteFieldProps };
export const AddressAutocompleteField = AddressAutocompleteFieldComponent;
